// The CRT pass: turns the terminal canvas into phosphor on the Clockers 64's curved glass.
// WebGL 1, two passes per frame:
//   1. persistence: new = max(terminal, previous * decay) at screen-pixel resolution, so a
//      vanishing cursor or a cleared line fades out like real phosphor;
//   2. glass: barrel curvature, scanlines, bloom, phosphor colour, vignette, a rounded-glass mask,
//      a little flicker and the power-on squash. The output is premultiplied light over the film.
// Without WebGL the terminal canvas is shown as is, with a CSS glow (see .crt-fallback).

import { H, W, type Terminal } from "./terminal";

const VS = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = vec2(aPos.x * 0.5 + 0.5, 0.5 - aPos.y * 0.5); // (0,0) = top left, like the canvas
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const PERSIST_FS = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uNew;
uniform sampler2D uPrev;
uniform float uDecay;
void main() {
  // Framebuffer rows run bottom-up; flip so the persistence texture keeps the canvas's top-down
  // orientation (the same as the uploaded terminal texture), frame after frame.
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  float n = texture2D(uNew, uv).a;
  float p = texture2D(uPrev, uv).a * uDecay;
  float v = max(n, p);
  gl_FragColor = vec4(v, v, v, v);
}`;

const GLASS_FS = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uTexSize;
uniform vec2 uRes;
uniform float uFlicker; // brightness factor, computed on the CPU (bounded; mediump-safe)
uniform float uPower;
uniform float uK;
uniform vec3 uPhosphor; // the glyph's core colour
uniform vec3 uHalo;     // the glow around it (redder, as in the film)

vec2 barrel(vec2 uv) {
  vec2 c = uv - 0.5;
  return 0.5 + c * (1.0 + uK * dot(c, c));
}
float roundBox(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
float I(vec2 uv) { return texture2D(uTex, uv).a; }

void main() {
  // the glass itself: a rounded rectangle, soft at its edge
  vec2 p = (vUv - 0.5) * uRes;
  float edge = roundBox(p, uRes * 0.5, uRes.y * 0.075);
  float mask = clamp(-edge / (uRes.y * 0.012), 0.0, 1.0);

  vec2 uv = barrel(vUv);
  // power-on: the picture grows from a bright horizontal line
  float pw = clamp(uPower, 0.0, 1.0);
  float squash = mix(0.004, 1.0, smoothstep(0.0, 0.6, pw));
  uv.y = 0.5 + (uv.y - 0.5) / squash;
  float inside = step(0.0, uv.x) * step(uv.x, 1.0) * step(0.0, uv.y) * step(uv.y, 1.0);

  vec2 tx = 1.0 / uTexSize;
  float i = I(uv);
  // bloom: two rings of taps
  float b = 0.0;
  for (int k = 0; k < 8; k++) {
    float a = float(k) * 0.785398;
    vec2 d = vec2(cos(a), sin(a));
    b += I(uv + d * tx * 2.6) * 0.55 + I(uv + d * tx * 6.5) * 0.45;
  }
  b /= 8.0;

  // scanlines: one per screen pixel row
  float row = fract(uv.y * uTexSize.y);
  float scan = 0.72 + 0.28 * sin(row * 3.14159);
  float bright = (1.0 + 2.5 * (1.0 - smoothstep(0.55, 1.0, pw))) * uFlicker; // flash while powering on

  vec3 col = uPhosphor * (i * scan) + uHalo * (b * 1.15) + vec3(1.0, 0.93, 0.8) * pow(i, 3.0) * 0.18;
  // vignette towards the curved edges
  vec2 c = vUv - 0.5;
  col *= 1.0 - 0.55 * dot(c, c) * 2.0;
  col *= bright * inside;

  // faint dark scanlines over the whole glass, so the screen reads as lit glass even when empty
  float dark = (1.0 - scan) * 0.22 * smoothstep(0.0, 0.3, pw);
  float a = clamp(max(max(col.r, col.g), col.b), 0.0, 1.0);
  a = max(a, dark);
  gl_FragColor = vec4(min(col, vec3(a)), a) * mask;
}`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader");
  return s;
}

function program(gl: WebGLRenderingContext, fs: string): WebGLProgram {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VS));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.bindAttribLocation(p, 0, "aPos");
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "link");
  return p;
}

function texture(gl: WebGLRenderingContext, filter: number): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

type Uniforms = Record<string, WebGLUniformLocation | null>;

export class CRT {
  /** Placed over the glass: holds the WebGL canvas, or the terminal canvas as the fallback. */
  readonly el: HTMLDivElement;
  /** True while the WebGL pass is in use (false before setup, after a failure, while lost). */
  webgl = false;
  /** 0 = off, 1 = fully on; animate it for the power-on and the refresh flicker. */
  power = 1;
  curvature = 0.06;
  /** false for reduced motion: no flicker */
  flicker = true;
  private glCanvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext | null = null;
  private persist: WebGLProgram | null = null;
  private glass: WebGLProgram | null = null;
  private uP: Uniforms = {};
  private uG: Uniforms = {};
  private termTex: WebGLTexture | null = null;
  private ping: { tex: WebGLTexture; fb: WebGLFramebuffer }[] = [];
  private pi = 0;
  private raf = 0;
  private last = 0;
  private pxW = 1;
  private pxH = 1;

  constructor(private term: Terminal) {
    this.el = document.createElement("div");
    this.el.className = "crt";
    this.glCanvas = document.createElement("canvas");
    term.canvas.classList.add("crt-fallback");
    this.glCanvas.addEventListener("webglcontextlost", (ev) => {
      ev.preventDefault(); // ask for restoration
      console.warn("[julkalenderspel] WebGL context lost; showing the plain screen");
      this.gl = null;
      this.useFallback();
    });
    this.glCanvas.addEventListener("webglcontextrestored", () => this.init());
    this.init();
  }

  /** (Re)create the WebGL pass; on any failure the plain terminal canvas is shown instead. */
  private init(): void {
    let gl: WebGLRenderingContext | null = null;
    try {
      gl = this.glCanvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false }) as WebGLRenderingContext | null;
      if (gl && !gl.isContextLost()) this.setup(gl);
      else gl = null;
    } catch (e) {
      console.warn("[julkalenderspel] WebGL CRT unavailable, using the plain screen", e);
      gl = null;
    }
    this.gl = gl;
    if (!gl) return this.useFallback();
    this.webgl = true;
    this.term.dirty = true; // re-upload the current contents
    this.el.replaceChildren(this.glCanvas);
    this.applySize();
  }

  private useFallback(): void {
    this.webgl = false;
    this.ping = [];
    this.el.replaceChildren(this.term.canvas);
  }

  private setup(gl: WebGLRenderingContext): void {
    this.persist = program(gl, PERSIST_FS);
    this.glass = program(gl, GLASS_FS);
    const look = (p: WebGLProgram, names: string[]) => Object.fromEntries(names.map((n) => [n, gl.getUniformLocation(p, n)]));
    this.uP = look(this.persist, ["uNew", "uPrev", "uDecay"]);
    this.uG = look(this.glass, ["uTex", "uTexSize", "uRes", "uFlicker", "uPower", "uK", "uPhosphor", "uHalo"]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.termTex = texture(gl, gl.NEAREST);
    this.ping = [];
    for (let k = 0; k < 2; k++) {
      const tex = texture(gl, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, W, H, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      const fb = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      this.ping.push({ tex, fb });
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Size of the drawing buffer: the glass as it appears on screen, in device pixels. */
  setPixelSize(w: number, h: number): void {
    this.pxW = Math.max(1, Math.min(4096, Math.round(w)));
    this.pxH = Math.max(1, Math.min(4096, Math.round(h)));
    this.applySize();
  }

  /** The WebGL canvas's current drawing-buffer size (for the checks). */
  get pixelSize(): [number, number] {
    return [this.glCanvas.width, this.glCanvas.height];
  }

  private applySize(): void {
    if (this.glCanvas.width !== this.pxW || this.glCanvas.height !== this.pxH) {
      this.glCanvas.width = this.pxW;
      this.glCanvas.height = this.pxH;
    }
  }

  start(): void {
    if (this.raf) return;
    const tick = (t: number) => {
      this.raf = requestAnimationFrame(tick);
      this.frame(t);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.last = 0;
  }

  get running(): boolean {
    return this.raf !== 0;
  }

  private frame(t: number): void {
    const changed = this.term.render(t);
    const gl = this.gl;
    if (!gl || !this.webgl || gl.isContextLost() || this.ping.length < 2) return;
    const dt = this.last ? Math.min(0.1, (t - this.last) / 1000) : 1 / 60;
    this.last = t;
    if (changed) {
      gl.bindTexture(gl.TEXTURE_2D, this.termTex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.term.canvas);
    }
    // 1. persistence
    const src = this.ping[this.pi];
    const dst = this.ping[1 - this.pi];
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
    gl.viewport(0, 0, W, H);
    gl.disable(gl.BLEND);
    gl.useProgram(this.persist);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.termTex);
    gl.uniform1i(this.uP.uNew, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, src.tex);
    gl.uniform1i(this.uP.uPrev, 1);
    gl.uniform1f(this.uP.uDecay, Math.pow(0.78, dt * 60));
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    this.pi = 1 - this.pi;
    // 2. glass
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.glCanvas.width, this.glCanvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.glass);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, dst.tex);
    const u = this.uG;
    const sec = t / 1000;
    // mains hum and a slow drift; phases wrapped in double precision on the CPU
    const flick = this.flicker ? 1 + 0.012 * Math.sin((sec * 377) % (2 * Math.PI)) + 0.006 * Math.sin((sec * 61) % (2 * Math.PI)) : 1;
    gl.uniform1i(u.uTex, 0);
    gl.uniform2f(u.uTexSize, W, H);
    gl.uniform2f(u.uRes, this.glCanvas.width, this.glCanvas.height);
    gl.uniform1f(u.uFlicker, flick);
    gl.uniform1f(u.uPower, this.power);
    gl.uniform1f(u.uK, this.curvature);
    // measured on the film's "READY.": pale amber core, red-orange halo
    gl.uniform3f(u.uPhosphor, 1.0, 0.8, 0.55);
    gl.uniform3f(u.uHalo, 1.0, 0.36, 0.12);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** For the checks: simulate a lost context. */
  loseContext(): boolean {
    const ext = this.gl?.getExtension("WEBGL_lose_context");
    ext?.loseContext();
    return !!ext;
  }
}
